
import React from 'react';
import { ItineraryEvent } from '@/types/itinerary';
import EventCard from '@/components/itinerary/EventCard';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useState } from 'react';

interface ListViewProps {
  events: ItineraryEvent[];
}

const ListView: React.FC<ListViewProps> = ({ events }) => {
  const { user } = useAuth();
  const [eventsWithUserNames, setEventsWithUserNames] = useState<ItineraryEvent[]>([]);

  useEffect(() => {
    // Function to fetch user details for each event creator
    const fetchUserDetails = async () => {
      const eventsToProcess = [...events];
      
      // Create a Set of unique user IDs to fetch
      const uniqueUserIds = new Set(eventsToProcess.map(event => event.created_by));
      
      // Only proceed if there are events and user IDs to fetch
      if (uniqueUserIds.size === 0) {
        setEventsWithUserNames(events);
        return;
      }
      
      try {
        // Fetch user profiles from Supabase
        const { data: profiles, error } = await supabase
          .from('profiles')
          .select('id, username')
          .in('id', Array.from(uniqueUserIds));
        
        if (error) {
          console.error('Error fetching user profiles:', error);
          setEventsWithUserNames(events);
          return;
        }
        
        // Create a mapping of user IDs to usernames
        const userMap = new Map();
        profiles?.forEach(profile => {
          userMap.set(
            profile.id, 
            profile.username || 'Unknown User'
          );
        });
        
        // Add user_name to each event
        const updatedEvents = eventsToProcess.map(event => ({
          ...event,
          user_name: userMap.get(event.created_by) || 'Unknown User'
        }));
        
        setEventsWithUserNames(updatedEvents);
      } catch (err) {
        console.error('Error in fetchUserDetails:', err);
        setEventsWithUserNames(events);
      }
    };
    
    fetchUserDetails();
  }, [events]);

  if (eventsWithUserNames.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No events scheduled for this day</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {eventsWithUserNames.map(event => (
        <EventCard 
          key={event.id} 
          event={event}
        />
      ))}
    </div>
  );
};

export default ListView;
